// Центральная система управления схемами с полной изоляцией
import { SCHEMA_CONFIGS, SchemaTypeConfig } from './SchemaTypeConfig';

export type SchemaType = 'main' | 'terminal' | 'knx' | 'rs485' | 'breaker' | 'ethernet' | 'floor_plan' | 'network';

export interface SchemaManagerContext {
  projectId?: string | number | null;
  userId?: string | number | null;
}

type SchemaViewport = { x: number; y: number; zoom: number };

export class SchemaManager {
  private config: SchemaTypeConfig;
  private schemaType: SchemaType;
  private schemaId: string | number;
  private context: SchemaManagerContext;
  private additionalInfo: Record<string, any> = {};

  constructor(schemaType: SchemaType, schemaId: string | number, context: SchemaManagerContext = {}) {
    this.schemaType = schemaType;
    this.schemaId = schemaId;
    this.context = context;
    this.config = SCHEMA_CONFIGS[schemaType];
    
    if (!this.config) {
      throw new Error(`Unknown schema type: ${schemaType}`);
    }
  }

  // ============= DATA ISOLATION =============
  
  /**
   * Получить namespace для БД данного типа схемы
   */
  getDataNamespace(): string {
    return this.config.dataNamespace;
  }

  /**
   * Получить изолированные API endpoints
   */
  getApiEndpoints() {
    return this.config.apiEndpoints;
  }

  /**
   * Получить общие API endpoints (connections)
   */
  getSharedEndpoints() {
    return this.config.sharedEndpoints;
  }

  /**
   * Построить API URL с учетом namespace (для изолированных данных)
   */
  buildApiUrl(endpoint: keyof typeof this.config.apiEndpoints, params?: Record<string, any>): string {
    const baseUrl = this.config.apiEndpoints[endpoint];
    const urlParams = new URLSearchParams();
    
    // Добавляем namespace и schema_id в параметры для изоляции
    urlParams.set('schema_type', this.schemaType);
    urlParams.set('schema_id', this.schemaId.toString());
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        urlParams.set(key, value.toString());
      });
    }

    return `${baseUrl}?${urlParams.toString()}`;
  }

  /**
   * Построить URL для общих данных (connections) - БЕЗ изоляции по schema_type
   */
  buildSharedApiUrl(endpoint: keyof typeof this.config.sharedEndpoints, params?: Record<string, any>): string {
    const baseUrl = this.config.sharedEndpoints[endpoint];
    const urlParams = new URLSearchParams();
    
    // Для connections НЕ добавляем schema_type - они общие для всех схем!
    // Добавляем только schema_id для фильтрации по проекту
    urlParams.set('schema_id', this.schemaId.toString());
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        urlParams.set(key, value.toString());
      });
    }

    return `${baseUrl}?${urlParams.toString()}`;
  }

  // ============= COMPONENT ISOLATION =============
  
  /**
   * Получить типы узлов для данной схемы
   */
  getNodeTypes(): string[] {
    return this.config.nodeTypes;
  }

  /**
   * Получить renderer для узлов
   */
  getNodeRenderer(): string {
    return this.config.nodeRenderer;
  }

  /**
   * Создать изолированные nodeTypes для ReactFlow
   */
  createReactFlowNodeTypes(): Record<string, any> {
    const nodeTypes: Record<string, any> = {};
    
    switch (this.schemaType) {
      case 'main':
        nodeTypes.device = this.createMainDeviceNode();
        nodeTypes.cable = this.createMainCableNode();
        nodeTypes.breaker = this.createMainBreakerNode();
        break;
        
      case 'terminal':
        nodeTypes.terminalBlock = this.createTerminalBlockNode();
        nodeTypes.terminalCable = this.createTerminalCableNode();
        break;
        
      case 'knx':
        nodeTypes.knxModule = this.createKnxModuleNode();
        nodeTypes.knxSensor = this.createKnxSensorNode();
        nodeTypes.knxLine = this.createKnxLineNode();
        break;
        
      case 'rs485':
        nodeTypes.rs485Device = this.createRs485DeviceNode();
        nodeTypes.rs485Converter = this.createRs485ConverterNode();
        nodeTypes.rs485Cable = this.createRs485CableNode();
        break;
    }
    
    return nodeTypes;
  }

  // Методы создания узлов (будут реализованы позже)
  private createMainDeviceNode() { /* Реализация */ }
  private createMainCableNode() { /* Реализация */ }
  private createMainBreakerNode() { /* Реализация */ }
  private createTerminalBlockNode() { /* Реализация */ }
  private createTerminalCableNode() { /* Реализация */ }
  private createKnxModuleNode() { /* Реализация */ }
  private createKnxSensorNode() { /* Реализация */ }
  private createKnxLineNode() { /* Реализация */ }
  private createRs485DeviceNode() { /* Реализация */ }
  private createRs485ConverterNode() { /* Реализация */ }
  private createRs485CableNode() { /* Реализация */ }

  // ============= TOOL ISOLATION =============
  
  /**
   * Получить доступные инструменты для данной схемы
   */
  getAvailableTools(): string[] {
    return this.config.tools;
  }

  /**
   * Получить обработчик инструмента
   */
  getToolHandler(toolName: string): string | null {
    return this.config.toolHandlers[toolName] || null;
  }

  /**
   * Выполнить действие инструмента
   */
  async executeTool(toolName: string, ...args: any[]): Promise<any> {
    const handler = this.getToolHandler(toolName);
    if (!handler) {
      console.warn(`No handler for tool: ${toolName} in schema: ${this.schemaType}`);
      return;
    }

    // Здесь будет диспетчер обработчиков инструментов
    return this.dispatchToolAction(handler, ...args);
  }

  private async dispatchToolAction(handlerName: string, ..._args: any[]): Promise<any> {
    console.log(`Executing ${handlerName} for ${this.schemaType} schema`);
    
    // Handle print functionality for all schema types
    if (handlerName === 'handleMainPrint' || 
        handlerName === 'handleTerminalPrint' || 
        handlerName === 'handleKnxPrint' || 
        handlerName === 'handleRs485Print') {
      return this.handlePrint();
    }
    
    // Handle grid snapping functionality
    if (handlerName === 'handleGridSnap') {
      return this.handleGridSnap();
    }
    
    // Handle layers functionality
    if (handlerName === 'handleLayers') {
      return this.handleLayers();
    }
    
    // TODO: Add other tool handlers here
    console.warn(`Handler ${handlerName} not implemented yet`);
  }

  /**
   * Handle PDF export for all schema types with A4 landscape tiling
   */
  private async handlePrint(): Promise<void> {
    let originalBackgroundStyle: string | undefined;
    
    try {
      // Find the ReactFlow wrapper element
      const schemaElement = document.querySelector('.react-flow');
      if (!schemaElement) {
        console.error('Schema canvas not found');
        return;
      }

      // Show loading state
      const originalCursor = document.body.style.cursor;
      document.body.style.cursor = 'wait';

      // Temporarily hide background dots and ensure connections are visible
      const backgroundElement = schemaElement.querySelector('.react-flow__background') as HTMLElement;
      originalBackgroundStyle = backgroundElement?.style.display;
      if (backgroundElement) {
        backgroundElement.style.display = 'none';
      }

      // Also hide React Flow attribution in original element
      const attributionElement = schemaElement.querySelector('.react-flow__attribution') as HTMLElement;
      const originalAttributionStyle = attributionElement?.style.display;
      if (attributionElement) {
        attributionElement.style.display = 'none';
      }

      // Ensure connections are visible by checking their z-index
      const connections = Array.from(schemaElement.querySelectorAll('.react-flow__edge')) as HTMLElement[];
      connections.forEach(connection => {
        if (connection.style.zIndex === '') {
          connection.style.zIndex = '1000';
        }
      });

      // Get the full schema bounds including connections
      const nodes = Array.from(schemaElement.querySelectorAll('.react-flow__node')) as HTMLElement[];
      
      let minX = 0, minY = 0, maxX = 0, maxY = 0;
      
      // Calculate bounds from nodes
      if (nodes.length > 0) {
        console.log('Found nodes:', nodes.length);
        
        // Try to get positions from transform if left/top are empty
        const nodePositions = nodes.map(node => {
          const left = parseFloat(node.style.left) || 0;
          const top = parseFloat(node.style.top) || 0;
          // Use actual rendered sizes; fallback to getBoundingClientRect
          const rect = node.getBoundingClientRect();
          const width = (node as HTMLElement).offsetWidth || rect.width || 100;
          const height = (node as HTMLElement).offsetHeight || rect.height || 100;
          
          // If left/top are 0, try to parse from transform
          if (left === 0 && top === 0 && node.style.transform) {
            const transformMatch = node.style.transform.match(/translate\(([^,]+),\s*([^)]+)\)/);
            if (transformMatch) {
              return {
                x: parseFloat(transformMatch[1]) || 0,
                y: parseFloat(transformMatch[2]) || 0,
                width,
                height
              };
            }
          }
          
          return { x: left, y: top, width, height };
        });
        
        if (nodePositions.length > 0) {
          minX = Math.min(...nodePositions.map(pos => pos.x));
          minY = Math.min(...nodePositions.map(pos => pos.y));
          maxX = Math.max(...nodePositions.map(pos => pos.x + pos.width));
          maxY = Math.max(...nodePositions.map(pos => pos.y + pos.height));
        }
      }

      // Add padding around the schema (in px)
      const padding = 10;
      const fullWidth = Math.max(maxX - minX + padding * 2, 800);
      const fullHeight = Math.max(maxY - minY + padding * 2, 600);
      
      console.log('Full schema bounds:', { minX, minY, maxX, maxY, fullWidth, fullHeight });
      console.log('Found connections:', connections.length);

      // Import html2canvas and jsPDF dynamically
      const html2canvas = (await import('html2canvas')).default;
      const jsPDF = (await import('jspdf')).default;

      // A4 landscape dimensions in mm (297 x 210)
      const A4_LANDSCAPE_WIDTH_MM = 297;
      
      // Convert mm to pixels (assuming 96 DPI)
      // NOTE: No direct need to convert here, keep placeholder if needed later
      
      // Define margins (mm) and available area
      const topBottomMarginMm = 10; // 1 cm
      const leftRightMarginMm = 10; // 1 cm
      const availableWidthMm = A4_LANDSCAPE_WIDTH_MM - (leftRightMarginMm * 2);
      const availableHeightMm = 210 - (topBottomMarginMm * 2);
      const pageAspect = availableWidthMm / availableHeightMm;

      // Calculate tile width in pixels so that each tile matches the printable A4 area aspect ratio
      // This guarantees that a full-height slice will exactly fill the available width in the PDF
      const tileWidthPx = Math.max(1, Math.floor(fullHeight * pageAspect));
      
      // Use full height for each tile (no vertical tiling)
      
      // Calculate number of pages needed (only horizontal)
      const pagesX = Math.ceil(fullWidth / tileWidthPx);
      const pagesY = 1; // Always 1 page vertically
      
              console.log('A4 Landscape horizontal tiling:', {
          tileWidthPx,
          fullHeight,
          pagesX,
          pagesY,
          totalPages: pagesX,
          fullWidth
        });

      // Create PDF with A4 landscape format
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });

      // Render each horizontal tile
      for (let px = 0; px < pagesX; px++) {
        // Add new page for all tiles except the first
        if (px !== 0) {
          pdf.addPage();
        }

        // Calculate tile boundaries (only horizontal)
        // Глобальное смещение всей области печати влево на 200px
        const globalLeftOffset = 200;
        const segmentStartX = (px * tileWidthPx) - globalLeftOffset;
        const segmentStartY = 0; // Always start from top
        const segmentWidthPx = Math.min(tileWidthPx, fullWidth - segmentStartX);
        const segmentHeightPx = fullHeight; // Use full height

        console.log(`Rendering horizontal tile ${px + 1}/${pagesX}:`, {
          segmentStartX,
          segmentStartY,
          segmentWidthPx,
          segmentHeightPx
        });

        // Render tile via html2canvas
        const tileCanvas = await html2canvas(schemaElement as HTMLElement, {
          backgroundColor: '#ffffff',
          scale: 2, // High DPI for sharp text
          useCORS: true,
          allowTaint: true,
          width: segmentWidthPx,
          height: segmentHeightPx,
          x: 0,
          y: 0,
          scrollX: 0,
          scrollY: 0,
          foreignObjectRendering: true,
          removeContainer: false,
          onclone: (clonedDoc) => {
            const clonedFlow = clonedDoc.querySelector('.react-flow') as HTMLElement;
            if (clonedFlow) {
              // Set white background and full dimensions
              clonedFlow.style.background = '#ffffff';
              clonedFlow.style.overflow = 'visible';
              clonedFlow.style.width = `${fullWidth}px`;
              clonedFlow.style.height = `${fullHeight}px`;

              // Hide background grid completely
              const bg = clonedFlow.querySelector('.react-flow__background') as HTMLElement;
              if (bg) {
                bg.style.display = 'none';
                bg.style.visibility = 'hidden';
                bg.style.opacity = '0';
                bg.style.pointerEvents = 'none';
                
                // Hide all grid elements inside background
                const gridElements = bg.querySelectorAll('*');
                gridElements.forEach(element => {
                  (element as HTMLElement).style.display = 'none';
                  (element as HTMLElement).style.visibility = 'hidden';
                  (element as HTMLElement).style.opacity = '0';
                });
              }

              // Hide React Flow attribution
              const attribution = clonedFlow.querySelector('.react-flow__attribution') as HTMLElement;
              if (attribution) attribution.style.display = 'none';

              // Transform viewport to show the correct horizontal tile
              const viewport = clonedFlow.querySelector('.react-flow__viewport') as HTMLElement;
              if (viewport) {
                viewport.style.transformOrigin = '0 0';
                viewport.style.transform = `translate(${(-minX + padding - segmentStartX)}px, ${(-minY + padding)}px) scale(1)`;
              }

              // Ensure SVG elements are visible
              const svgElements = clonedFlow.querySelectorAll('svg');
              svgElements.forEach(svg => {
                (svg as SVGElement).style.display = 'block';
                (svg as SVGElement).style.visibility = 'visible';
                (svg as SVGElement).style.opacity = '1';
              });

              // Ensure edge elements are visible
              const edgeElements = clonedFlow.querySelectorAll('.react-flow__edge');
              edgeElements.forEach(edge => {
                (edge as HTMLElement).style.display = 'block';
                (edge as HTMLElement).style.visibility = 'visible';
                (edge as HTMLElement).style.opacity = '1';
                (edge as HTMLElement).style.zIndex = '1000';
              });
            }
          }
        });

        // Convert canvas to image
        const tileImg = tileCanvas.toDataURL('image/jpeg', 0.82);

        // Calculate scaling with reasonable margins
        const imageAspectRatio = segmentWidthPx / segmentHeightPx;
        
        // Define margins in mm - 1cm (10mm) on all sides
        const topBottomMargin = 10; // 10mm top and bottom
        const leftRightMargin = 10; // 10mm left and right
        
        // Calculate available space
        const availableWidth = A4_LANDSCAPE_WIDTH_MM - (leftRightMargin * 2);
        
        let imageWidth: number;
        let imageHeight: number;
        let imageX: number;
        let imageY: number;
        
        // Try to maximize usage of available space
        const availableHeight = 210 - (topBottomMargin * 2); // 210mm is A4 height
        
        // First, try to scale to fit available height
        imageHeight = availableHeight;
        imageWidth = availableHeight * imageAspectRatio;
        imageX = leftRightMargin;
        imageY = topBottomMargin;
        
        // If width exceeds available width, scale down proportionally
        if (imageWidth > availableWidth) {
          const scaleFactor = availableWidth / imageWidth;
          imageWidth = availableWidth;
          imageHeight *= scaleFactor;
          imageX = leftRightMargin;
          imageY = topBottomMargin + (availableHeight - imageHeight) / 2; // Center vertically
        }

        // Add image to PDF page
        pdf.setPage(px + 1);
        pdf.addImage(tileImg, 'JPEG', imageX, imageY, imageWidth, imageHeight);
      }

      // Generate filename with timestamp
      const now = new Date();
      const moscowTime = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/Moscow',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }).format(now).replace(/[.,]/g, '-').replace(/\s/g, '-');
      
      // Add terminal block name for terminal schemas
      const additionalSuffix = this.schemaType === 'terminal' && this.additionalInfo.terminalBlockName 
        ? `_${this.additionalInfo.terminalBlockName}` 
        : '';
      
      const fileName = `schema_${this.schemaType}_${this.schemaId}_${moscowTime}${additionalSuffix}_A4_landscape.pdf`;
      console.log('📄 Final filename:', fileName);
      
      // Save PDF
      pdf.save(fileName);

      // Restore background and cursor
      if (backgroundElement && originalBackgroundStyle !== undefined) {
        backgroundElement.style.display = originalBackgroundStyle;
      }
      if (attributionElement && originalAttributionStyle !== undefined) {
        attributionElement.style.display = originalAttributionStyle;
      }
      document.body.style.cursor = originalCursor;
      
      console.log(`PDF exported successfully. A4 Landscape: ${pagesX} pages (horizontal tiling only)`);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      
      // Restore background on error
      const backgroundElement = document.querySelector('.react-flow__background') as HTMLElement;
      if (backgroundElement && originalBackgroundStyle !== undefined) {
        backgroundElement.style.display = originalBackgroundStyle;
      }
      const attributionElement = document.querySelector('.react-flow__attribution') as HTMLElement;
      if (attributionElement) {
        attributionElement.style.display = 'block'; // Restore to default
      }
      document.body.style.cursor = 'default';
      
      throw error;
    }
  }

  /**
   * Handle grid snapping functionality
   */
  private async handleGridSnap(): Promise<void> {
    // This will be handled by the UniversalSchemaEditor component
    // The actual grid snapping logic is implemented there
    console.log('Grid snapping toggled for schema:', this.schemaType);
  }

  /**
   * Handle layers panel toggle
   */
  private async handleLayers(): Promise<void> {
    // This will be handled by the UniversalSchemaEditor component
    // The actual layers panel logic is implemented there
    console.log('Layers panel toggled for schema:', this.schemaType);
  }

  // ============= STORAGE ISOLATION =============
  
  /**
   * Получить изолированный ключ для localStorage
   */
  getStorageKey(keyType: string): string {
    return `schema-${keyType}-${this.schemaType}-${this.schemaId}`;
  }

  /**
   * Получить ключ, изолированный по пользователю и проекту.
   * Старый getStorageKey оставлен для совместимости preferences и миграции viewport.
   */
  getScopedStorageKey(keyType: string): string {
    const userId = this.context.userId ?? 'anonymous';
    const projectId = this.context.projectId ?? 'unknown';
    return `schema-${keyType}-v2-user-${userId}-project-${projectId}-${this.schemaType}-${this.schemaId}`;
  }

  private getLegacyViewportStorageKeys(): string[] {
    const keys = [this.getStorageKey('viewport')];
    
    if (this.schemaType === 'terminal') {
      keys.push(`terminal-schema-viewport-${this.schemaId}`);
    }
    
    return keys;
  }

  private isValidViewport(viewport: unknown): viewport is SchemaViewport {
    if (!viewport || typeof viewport !== 'object') return false;
    const value = viewport as Partial<SchemaViewport>;
    return typeof value.x === 'number' &&
      typeof value.y === 'number' &&
      typeof value.zoom === 'number' &&
      Number.isFinite(value.x) &&
      Number.isFinite(value.y) &&
      Number.isFinite(value.zoom) &&
      value.zoom > 0;
  }

  /**
   * Сохранить viewport схемы в изолированное localStorage-хранилище.
   */
  saveViewport(viewport: SchemaViewport): void {
    if (!this.config.isolatedStorage || !this.isValidViewport(viewport)) return;
    
    try {
      localStorage.setItem(this.getScopedStorageKey('viewport'), JSON.stringify(viewport));
    } catch (e) {
      console.error('Error saving schema viewport:', e);
    }
  }

  /**
   * Восстановить viewport схемы. Сначала читаем новый user/project scoped ключ,
   * затем старые ключи, чтобы не потерять уже сохраненные положения.
   */
  restoreViewport(): SchemaViewport | null {
    if (!this.config.isolatedStorage) return null;
    
    const keys = [this.getScopedStorageKey('viewport'), ...this.getLegacyViewportStorageKeys()];
    
    for (const key of keys) {
      const saved = localStorage.getItem(key);
      if (!saved) continue;
      
      try {
        const viewport = JSON.parse(saved);
        if (this.isValidViewport(viewport)) {
          if (key !== this.getScopedStorageKey('viewport')) {
            this.saveViewport(viewport);
          }
          return viewport;
        }
      } catch (e) {
        console.error('Error parsing saved viewport:', e);
      }
    }
    
    return null;
  }

  /**
   * Сохранить настройки схемы
   */
  saveSchemaPreferences(preferences: Record<string, any>): void {
    if (this.config.isolatedStorage) {
      const key = this.getStorageKey('preferences');
      localStorage.setItem(key, JSON.stringify(preferences));
    }
  }

  /**
   * Восстановить настройки схемы
   */
  restoreSchemaPreferences(): Record<string, any> | null {
    if (!this.config.isolatedStorage) return null;
    
    const key = this.getStorageKey('preferences');
    const saved = localStorage.getItem(key);
    
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Error parsing saved preferences:', e);
        return null;
      }
    }
    
    return null;
  }

  // ============= LIBRARY ISOLATION =============
  
  /**
   * Получить тип библиотеки элементов
   */
  getLibraryType(): string {
    return this.config.libraryType;
  }

  /**
   * Получить доступные типы устройств
   */
  getAllowedDeviceTypes(): string[] {
    return this.config.allowedDeviceTypes;
  }

  // ============= UI ISOLATION =============
  
  /**
   * Получить конфигурацию UI для данной схемы
   */
  getUIConfig() {
    return {
      name: this.config.name,
      backgroundGrid: this.config.backgroundGrid,
      canvasColor: this.config.canvasColor,
      electricalPanelRequired: this.config.electricalPanelRequired,
      availablePanels: this.config.availablePanels
    };
  }

  /**
   * Проверить требования схемы
   */
  validateSchemaRequirements(context: any): boolean {
    if (this.config.electricalPanelRequired && !context.electricalPanelId) {
      return false;
    }
    
    return true;
  }

  // ============= UTILITY METHODS =============
  
  /**
   * Получить полную конфигурацию
   */
  getConfig(): SchemaTypeConfig {
    return this.config;
  }

  /**
   * Получить тип схемы
   */
  getSchemaType(): SchemaType {
    return this.schemaType;
  }

  /**
   * Получить ID схемы
   */
  getSchemaId(): string | number {
    return this.schemaId;
  }

  /**
   * Установить дополнительную информацию для формирования имени файла
   */
  setAdditionalInfo(info: Record<string, any>): void {
    this.additionalInfo = { ...this.additionalInfo, ...info };
  }

  /**
   * Получить дополнительную информацию
   */
  getAdditionalInfo(): Record<string, any> {
    return this.additionalInfo;
  }
}

// ============= FACTORY METHODS =============

/**
 * Создать менеджер схемы
 */
export function createSchemaManager(schemaType: SchemaType, schemaId: string | number, context?: SchemaManagerContext): SchemaManager {
  return new SchemaManager(schemaType, schemaId, context);
}

/**
 * Получить доступные типы схем
 */
export function getAvailableSchemaTypes(): SchemaType[] {
  return Object.keys(SCHEMA_CONFIGS) as SchemaType[];
}

/**
 * Проверить поддержку типа схемы
 */
export function isSchemaTypeSupported(schemaType: string): schemaType is SchemaType {
  return schemaType in SCHEMA_CONFIGS;
} 